<template>
    <div class="main-content">
        <div class="container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="back-link">
                    <a href="/#/gene/list"><i class="el-icon-back"></i> Back to Gene List</a>
                </div>
                <h1>{{ geneInfo.geneSymbol || 'Gene Detail' }}</h1>
                <p class="lead">Detailed information and analysis for this gene.</p>
            </div>

            <!-- Overview Card -->
            <div class="info-card">
                <div class="card-header">
                    <div class="title-row">
                        <span><i class="el-icon-info"></i> Gene Overview</span>
                    </div>
                </div>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label">Gene Symbol</span>
                        <span class="value highlight">{{ geneInfo.geneSymbol }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Ensembl ID</span>
                        <span class="value">{{ geneInfo.ensemblId }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Location</span>
                        <span class="value">{{ geneInfo.geneLocation }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Type</span>
                        <span class="value">{{ geneInfo.geneType }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Full Name</span>
                        <span class="value">{{ geneInfo.fullName }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Synonyms</span>
                        <span class="value">{{ geneInfo.synonyms }}</span>
                    </div>
                    <!-- External Links Row -->
                    <div class="info-item links-row">
                        <span class="label">More Information</span>
                        <div class="links-container">
                            <a :href="'https://asia.ensembl.org/Homo_sapiens/Gene/Summary?db=core;g=' + geneInfo.geneSymbol" target="_blank" class="external-link">ENSEMBL</a>
                            <a :href="'https://www.genecards.org/cgi-bin/carddisp.pl?gene=' + geneInfo.geneSymbol" target="_blank" class="external-link">GENECARDS</a>
                            <a :href="'https://www.ncbi.nlm.nih.gov/gene/?term=' + geneInfo.geneSymbol" target="_blank" class="external-link">NCBI</a>
                        </div>
                    </div>
                </div>
            </div>

            <el-tabs v-model="activeTab" class="mt-4 custom-tabs" @tab-click="handleTabClick">
                <!-- Gene Tab -->
                <el-tab-pane label="Gene" name="Gene" style="padding-top: 20px;">
                    <div class="section-container mt-4">
                        <h3 class="section-title" style="margin-bottom: 20px;"><i class="el-icon-s-grid"></i> Gene Correlation</h3>

                        <!-- Filter Section -->
                        <div class="filter-container mb-3">
                            <el-form :inline="true" :model="geneCombinedFilters" class="demo-form-inline" size="small">
                                <el-form-item label="Adj. P-value">
                                    <el-input v-model="geneCombinedFilters.adjustedP" placeholder="< Value"></el-input>
                                </el-form-item>
                                <el-form-item label="Corr">
                                    <el-input v-model="geneCombinedFilters.corr" placeholder="> Value"></el-input>
                                </el-form-item>
                                <el-form-item label="Cell Type">
                                    <el-select v-model="geneCombinedFilters.cellType" placeholder="Cell Type" clearable filterable size="small">
                                        <el-option
                                            v-for="item in geneCombinedFilterOptions.cellTypes"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <!-- <el-form-item label="Tissue">
                                    <el-input v-model="geneCombinedFilters.tissue" placeholder="Tissue" clearable></el-input>
                                </el-form-item> -->
                                <el-form-item label="Gene 1">
                                    <el-select v-model="geneCombinedFilters.gene1" placeholder="Gene 1" clearable filterable size="small">
                                        <el-option
                                            v-for="item in geneCombinedFilterOptions.gene1"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 2">
                                    <el-select v-model="geneCombinedFilters.gene2" placeholder="Gene 2" clearable filterable size="small">
                                        <el-option
                                            v-for="item in geneCombinedFilterOptions.gene2"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleGeneCombinedFilterSearch" icon="el-icon-search">Search</el-button>
                                    <el-button @click="handleGeneCombinedFilterReset" icon="el-icon-refresh">Reset</el-button>
                                    <el-button type="warning" @click="handleGeneExport" icon="el-icon-download">Download</el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <div class="table-card">
                            <el-table :data="geneTableData" style="width: 100%" stripe border>
                                <el-table-column prop="gene1" label="Gene 1" sortable />
                                <el-table-column prop="gene2" label="Gene 2" sortable />
                                <el-table-column prop="pvalue" label="P-value" sortable />
                                <el-table-column prop="adjustedP" label="Adjusted P" sortable />
                                <el-table-column prop="corr" label="Corr" sortable />
                                <el-table-column prop="cellType" label="Cell Type" sortable />
                                <el-table-column prop="tissue" label="Tissue" sortable />
                                <el-table-column prop="data" label="Data" sortable>
                                    <template slot-scope="scope">
                                        <el-link type="primary" @click="handleDataClick(scope.row)">{{ scope.row.data }}</el-link>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="type" label="Type" sortable />
                                <!-- <el-table-column prop="dataName" label="Data Name" sortable /> -->
                            </el-table>
                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleGeneSizeChange"
                                    @current-change="handleGenePageChange"
                                    :current-page="genePagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="genePagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="genePagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <!-- Module Tab -->
                <el-tab-pane label="Module" name="Module" style="padding-top: 20px;">
                    <div class="section-container mt-4">
                        <h3 class="section-title"><i class="el-icon-connection"></i> Module Data</h3>
                        <div class="table-card">
                            <el-table :data="moduleTableData" style="width: 100%" stripe border>
                                <el-table-column prop="geneSymbol" label="Gene.Symbol" sortable />
                                <el-table-column prop="module" label="Module" sortable>
                                    <template slot-scope="scope">
                                        <el-link type="primary" @click="handleModuleClick(scope.row)">{{ scope.row.module }}</el-link>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="cellType" label="Cell_Type" sortable />
                                <el-table-column prop="ensemblId" label="Ensembl.ID" sortable />
                                <el-table-column prop="geneLocation" label="Gene.Location" sortable />
                                <el-table-column prop="geneType" label="Gene.Type" sortable />
                                <el-table-column prop="data" label="Data" sortable />
                            </el-table>
                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleModuleSizeChange"
                                    @current-change="handleModulePageChange"
                                    :current-page="modulePagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="modulePagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="modulePagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>
    </div>
</template>

<script>
export default {
    name: "GeneDetail",
    data() {
        return {
            geneInfo: {},
            activeTab: "Gene",
            
            // Gene Tab Data
            geneTableData: [],
            genePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            geneCombinedFilters: {
                adjustedP: '',
                corr: '',
                cellType: '',
                tissue: '',
                gene1: '',
                gene2: ''
            },
            geneCombinedFilterOptions: {
                gene1: [],
                gene2: [],
                cellTypes: []
            },

            // Module Tab Data
            moduleTableData: [],
            modulePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            }
        };
    },
    created() {
        this.getGeneDetail();
    },
    methods: {
        async getGeneDetail() {
            try {
                const geneId = this.$route.params.id;
                const species = this.$route.query.species || 'Human';
                const res = await this.$ajax.get(`/gene/detail/${geneId}?species=${species}`);
                if (res.code === 200) {
                    this.geneInfo = res.data;
                    this.getGeneCombinedFilterOptions();
                    // Initial load of active tab data
                    this.handleTabClick();
                }
            } catch (error) {
                console.error("Failed to fetch gene detail:", error);
            }
        },

        handleTabClick() {
            if (this.activeTab === 'Gene') {
                this.getGeneCombinedData();
            } else if (this.activeTab === 'Module') {
                this.getModuleData();
            }
        },

        // Gene Tab Methods
        async getGeneCombinedFilterOptions() {
            if (!this.geneInfo.geneSymbol) return;
            try {
                const res = await this.$ajax.get(`/gene/combined/filters?gene=${this.geneInfo.geneSymbol}`);
                if (res.code === 200) {
                    this.geneCombinedFilterOptions = res.data;
                }
            } catch (error) {
                console.error("Failed to fetch gene combined filter options:", error);
            }
        },

        async getGeneCombinedData() {
            if (!this.geneInfo.geneSymbol) return;
            try {
                const params = new URLSearchParams();
                params.append('gene', this.geneInfo.geneSymbol);
                params.append('pageNum', this.genePagination.pageNum);
                params.append('pageSize', this.genePagination.pageSize);

                if (this.geneCombinedFilters.adjustedP) params.append('maxAdjustedP', this.geneCombinedFilters.adjustedP);
                if (this.geneCombinedFilters.corr) params.append('minCorr', this.geneCombinedFilters.corr);
                if (this.geneCombinedFilters.cellType) params.append('cellType', this.geneCombinedFilters.cellType);
                if (this.geneCombinedFilters.tissue) params.append('tissue', this.geneCombinedFilters.tissue);
                if (this.geneCombinedFilters.gene1) params.append('gene1', this.geneCombinedFilters.gene1);
                if (this.geneCombinedFilters.gene2) params.append('gene2', this.geneCombinedFilters.gene2);

                const res = await this.$ajax.get(`/gene/combined/page?${params.toString()}`);
                if (res.code === 200) {
                    this.geneTableData = res.data.records || [];
                    this.genePagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch gene combined data:", error);
            }
        },

        handleGeneSizeChange(size) {
            this.genePagination.pageSize = size;
            this.getGeneCombinedData();
        },

        handleGenePageChange(page) {
            this.genePagination.pageNum = page;
            this.getGeneCombinedData();
        },

        handleGeneExport() {
            const params = new URLSearchParams();
            params.append('gene', this.geneInfo.geneSymbol);
            if (this.geneCombinedFilters.adjustedP) params.append('maxAdjustedP', this.geneCombinedFilters.adjustedP);
            if (this.geneCombinedFilters.corr) params.append('minCorr', this.geneCombinedFilters.corr);
            if (this.geneCombinedFilters.cellType) params.append('cellType', this.geneCombinedFilters.cellType);
            if (this.geneCombinedFilters.tissue) params.append('tissue', this.geneCombinedFilters.tissue);
            if (this.geneCombinedFilters.gene1) params.append('gene1', this.geneCombinedFilters.gene1);
            if (this.geneCombinedFilters.gene2) params.append('gene2', this.geneCombinedFilters.gene2);

            const url = `/api/gene/combined/export?${params.toString()}`;
            window.open(url, '_blank');
        },

        handleGeneCombinedFilterSearch() {
            this.genePagination.pageNum = 1;
            this.getGeneCombinedData();
        },

        handleGeneCombinedFilterReset() {
            this.geneCombinedFilters = {
                adjustedP: '',
                corr: '',
                cellType: '',
                tissue: '',
                gene1: '',
                gene2: ''
            };
            this.handleGeneCombinedFilterSearch();
        },

        // Module Tab Methods
        async getModuleData() {
            if (!this.geneInfo.geneSymbol) return;
            try {
                const params = new URLSearchParams();
                params.append('gene', this.geneInfo.geneSymbol);
                params.append('species', this.$route.query.species || 'Human');
                params.append('pageNum', this.modulePagination.pageNum);
                params.append('pageSize', this.modulePagination.pageSize);

                const res = await this.$ajax.get(`/gene/module/page?${params.toString()}`);
                if (res.code === 200) {
                    this.moduleTableData = res.data.records || [];
                    this.modulePagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch module data:", error);
            }
        },

        handleModuleSizeChange(size) {
            this.modulePagination.pageSize = size;
            this.getModuleData();
        },

        handleModulePageChange(page) {
            this.modulePagination.pageNum = page;
            this.getModuleData();
        },

        handleDataClick(row) {
            const targetId = row.singleCellId || row.id;
            if (targetId) {
                this.$router.push(`/singleCell/detail/${targetId}`);
            } else {
                this.$message.warning("Single Cell ID not found.");
            }
        },

        handleModuleClick(row) {
            // Priority: singleCellId, then datasetId, then id
            const targetId = row.singleCellId || row.datasetId || row.id;

            if (targetId) {
                this.$router.push({
                    path: `/singleCell/detail/${targetId}`,
                    query: {
                        tab: 'Module',
                        moduleName: row.module,
                        cellType: row.cellType
                    }
                });
            } else {
                this.$message.warning("Single Cell ID not found for this module.");
            }
        }
    }
};
</script>

<style lang="less" scoped>
:root {
    --bg: #dfe4ed;
    --panel: #f7f9fc;
    --navy: #0e1a28;
    --muted: #4a586b;
    --accent: #5d87b5;
    --border: #c7d1dd;
    --shadow: 0 20px 44px rgba(14, 26, 40, 0.18);
    --primary: #0e1a28;
}

.main-content {
    background: #dfe4ed;
    min-height: 100vh;
    padding-bottom: 60px;
    font-family: 'Inter', sans-serif;
    color: #0e1a28;
    padding-top: 30px;
}

.container {
    padding: 0 20px;
}

/* Header Section */
.header-section {
    padding: 0px 0 20px;
    text-align: center;
    
    .back-link {
        margin-bottom: 12px;
        text-align: left;
        a {
            color: var(--accent);
            text-decoration: none;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: transform 0.2s;
            &:hover {
                transform: translateX(-4px);
            }
        }
    }

    h1 {
        font-family: 'Playfair Display', serif;
        font-size: 36px;
        color: var(--navy);
        margin: 0 0 8px;
        font-weight: 700;
        letter-spacing: -0.5px;
    }

    .lead {
        font-size: 16px;
        color: var(--muted);
        max-width: 700px;
        margin: 0 auto;
        line-height: 1.5;
    }
}

/* Info Card */
.info-card {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    padding: 24px;
    margin-bottom: 24px;

    .card-header {
        font-family: 'Playfair Display', serif;
        font-size: 20px;
        font-weight: 700;
        color: var(--navy);
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        i { color: var(--accent); }
    }

    .info-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .info-item {
        display: grid;
        grid-template-columns: 140px 1fr;
        align-items: baseline;
        padding: 8px 0;
        border-bottom: 1px solid #eee;

        &:last-child {
            border-bottom: none;
        }

        .label {
            font-weight: 600;
            color: var(--muted);
            font-size: 14px;
        }

        .value {
            color: var(--navy);
            font-size: 15px;
            
            &.highlight {
                color: var(--accent);
                font-weight: 600;
            }
        }
    }

    .links-row {
        grid-template-columns: 140px 1fr;
        padding: 12px 0;
        
        .links-container {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
        }

        .external-link {
            display: inline-flex;
            align-items: center;
            padding: 6px 16px;
            background: #f8fbff;
            border: 1px solid var(--border);
            border-radius: 8px;
            color: var(--navy);
            text-decoration: none;
            font-weight: 600;
            font-size: 13px;
            letter-spacing: 0.3px;
            transition: all 0.3s ease;

            &:hover {
                background: var(--accent);
                color: white;
                border-color: var(--accent);
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(93, 135, 181, 0.2);
            }
        }
    }
}

/* Section Container */
.section-container {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    padding: 24px;
    margin-bottom: 24px;

    .section-title {
        font-family: 'Playfair Display', serif;
        font-size: 22px;
        font-weight: 700;
        color: var(--navy);
        margin: 0 0 20px;
        display: flex;
        align-items: center;
        gap: 10px;
        i { color: var(--accent); }
    }
}

/* Table Styling */
.table-card {
    :deep(.el-table) {
        border-radius: 8px;
        overflow: hidden;
        
        th {
            background-color: #f8f9fa;
            color: var(--navy);
            font-weight: 600;
        }
    }

    .pagination-wrapper {
        margin-top: 20px;
        display: flex;
        justify-content: flex-end;
    }
}

/* Tabs */
.custom-tabs {
    :deep(.el-tabs__header) {
        margin-bottom: 0;
    }
    
    :deep(.el-tabs__nav-wrap::after) {
        height: 1px;
        background-color: var(--border);
    }
    
    :deep(.el-tabs__item) {
        font-size: 16px;
        font-weight: 600;
        color: var(--muted);
        &.is-active {
            color: var(--accent);
        }
    }
}
</style>